// apps/web/src/components/turnstile.tsx — Phase 4 plan 04-03.
//
// Cloudflare Turnstile widget wrapper. Per D-26 the production
// bundle loads `https://challenges.cloudflare.com/turnstile/v0/api.js`
// (eagerly via <script> in `apps/web/index.html`); the dev bundle
// skips the script and the dev-bypass returns true for an empty
// token. The component itself only renders the widget in
// `import.meta.env.MODE === "production"` and exposes:
//
//   - onToken(token: string): invoked by `turnstile.render`'s
//     `callback` whenever the user solves the challenge.
//   - onExpire(): invoked by `turnstile.render`'s
//     'expired-callback' when the token TTL elapses.
//   - getToken(): the latest captured token. Returns `""` in dev
//     (the server's dev-bypass recognizes an empty token).
//
// Per the threat model T-04-27: in dev the placeholder renders
// WITHOUT calling `turnstile.render`, so a malicious page could
// not invoke the dev-bypass via the test path.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface TurnstileHandle {
  getToken: () => string;
}

/**
 * Reads the runtime mode + site key from `import.meta.env`. The
 * helper exists so the test suite can mock the env without Vite's
 * compile-time substitution biting: tests that need the
 * production path replace the helper via `vi.mock`; tests that
 * stay in dev mode simply rely on the default `MODE === "test"`
 * from vitest.
 */
export function getTurnstileEnv(): {
  mode: string;
  sitekey: string | undefined;
} {
  // Test-only override: the `__gastioTurnstileEnv` global lets the
  // test suite flip the mode + sitekey without fighting Vite's
  // `import.meta.env` snapshot (which is frozen at module load
  // time and ignores `vi.stubEnv`).
  const override = (
    globalThis as { __gastioTurnstileEnv?: { mode?: string; sitekey?: string } }
  ).__gastioTurnstileEnv;
  if (override) {
    return {
      mode: override.mode ?? "production",
      sitekey: override.sitekey,
    };
  }
  // We read via index access to avoid Vite's static-replacement
  // optimization on the dot-form. The `as unknown as Record` cast
  // is required because the typed `ImportMetaEnv` does not
  // declare every key the test might inject.
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return {
    mode: env["MODE"] ?? "production",
    sitekey: env["VITE_TURNSTILE_SITE_KEY"],
  };
}

/** Test-only setter for the override used by `getTurnstileEnv`. */
export function __setTurnstileEnvForTest(
  value: { mode?: string; sitekey?: string } | undefined,
): void {
  if (value === undefined) {
    delete (globalThis as { __gastioTurnstileEnv?: unknown })
      .__gastioTurnstileEnv;
    return;
  }
  (
    globalThis as { __gastioTurnstileEnv?: { mode?: string; sitekey?: string } }
  ).__gastioTurnstileEnv = value;
}

export interface TurnstileProps {
  onToken: (token: string) => void;
  onExpire: () => void;
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  function Turnstile({ onToken, onExpire }, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const tokenRef = useRef<string>("");
    const [widgetId, setWidgetId] = useState<string | null>(null);
    const [errored, setErrored] = useState(false);

    // Determine the runtime mode. Vite exposes `import.meta.env.MODE`
    // and `import.meta.env.VITE_TURNSTILE_SITE_KEY`; we read them
    // once on mount so a production build never flips into dev mode
    // (the threat model requires the dev placeholder to NEVER
    // invoke turnstile.render). The `getRuntimeMode` wrapper is
    // exported separately so the test suite can mock the runtime
    // mode without a Vite build-time substitution.
    const { mode, sitekey } = getTurnstileEnv();

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => tokenRef.current,
      }),
      [],
    );

    const handleToken = useCallback(
      (token: string) => {
        tokenRef.current = token;
        setErrored(false);
        onToken(token);
      },
      [onToken],
    );

    const handleExpire = useCallback(() => {
      tokenRef.current = "";
      onExpire();
    }, [onExpire]);

    useEffect(() => {
      if (mode !== "production") return;
      if (!containerRef.current) return;
      if (typeof window === "undefined") return;
      if (!window.turnstile) {
        setErrored(true);
        return;
      }
      if (!sitekey) {
        setErrored(true);
        return;
      }
      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey,
          callback: handleToken,
          "expired-callback": handleExpire,
        });
        setWidgetId(id);
      } catch {
        setErrored(true);
      }
      return () => {
        if (widgetId && window.turnstile) {
          try {
            window.turnstile.remove(widgetId);
          } catch {
            /* ignore */
          }
        }
      };
      // We intentionally exclude widgetId from deps — the cleanup
      // runs once on unmount and we want a stable render() call.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, sitekey, handleToken, handleExpire]);

    if (mode !== "production") {
      return (
        <div
          data-testid="turnstile-placeholder"
          className="text-text/40 text-xs italic"
        >
          Captcha deaktiviert (Dev)
        </div>
      );
    }

    if (errored) {
      return (
        <div
          role="alert"
          data-testid="turnstile-error"
          className="text-sm text-text bg-surface border border-divider rounded-md p-3"
        >
          Sicherheitsüberprüfung konnte nicht geladen werden. Bitte Seite neu
          laden.
        </div>
      );
    }

    return <div ref={containerRef} data-testid="turnstile-widget" />;
  },
);
